import { toLower, toString } from 'lodash-es';
import { useMemo } from 'react';
import { VersionStatus } from '../pages/application-details-page/ApplicationDetailsPage.model';

export interface VersionStatusData {
  readonly status?: string;
  readonly statusLabel?: string;
}

export interface EntityStatusData {
  readonly status?: {
    readonly type?: string;
  };
}

export interface UseVersionStatusProps {
  readonly versionData?: VersionStatusData | null;
  readonly entityData?: EntityStatusData | null;
  readonly statusLabel?: string;
}

export interface StatusProps {
  readonly status?: string;
  readonly statusLabel?: string;
}

/**
 * Hook to handle version status logic for system packages, device applications, and OS versions
 */
export function useVersionStatus({ versionData, entityData, statusLabel }: UseVersionStatusProps): StatusProps {
  const versionStatus = useMemo(
    () =>
      toLower(versionData?.status as VersionStatus) === VersionStatus.PUBLISHED
        ? VersionStatus.PUBLISHED
        : VersionStatus.DEPRECATED,
    [versionData?.status],
  );

  const entityStatusType = useMemo(
    () => (entityData?.status?.type ? toLower(entityData.status.type as VersionStatus) : undefined),
    [entityData?.status?.type],
  );

  const { statusVersion, statusLabel: calculatedStatusLabel } = useMemo(() => {
    if (!entityData || !versionData || !entityStatusType) {
      return { statusVersion: undefined, statusLabel: undefined };
    }

    const version = entityStatusType === VersionStatus.DEPRECATED ? 'neutral' : versionStatus;

    return {
      statusVersion: toString(version),
      statusLabel: toString(statusLabel),
    };
  }, [entityData, versionData, entityStatusType, versionStatus, statusLabel]);

  return useMemo(
    () => ({
      status: statusVersion,
      statusLabel: calculatedStatusLabel,
    }),
    [statusVersion, calculatedStatusLabel],
  );
}
